import { ClockIcon, GearIcon, GlobalIcon, MarkerInfoIcon, ReviewIcon, UserIcon } from '@assets/svg';
import { IDropdownOption, Nullable } from '@common/interfaces';
import { PseudoFile } from '@components/UploadField';
import * as Yup from 'yup';

export interface DetailsStepValues {
  address: string;
  city_name: Nullable<IDropdownOption>;
  country: Nullable<IDropdownOption>;
  depot_number: Nullable<IDropdownOption>;
  email: string;
  image: Array<File | PseudoFile>;
  is_active: boolean;
  latitude: string;
  longitude: string;
  name: string;
  status: Nullable<IDropdownOption>;
}

export interface RulesStepValues {
  global: Nullable<IDropdownOption>;
  agencies: IDropdownOption[];
}

export interface TimeStepValues {
  start_time: Nullable<Date>;
  end_time: Nullable<Date>;
  weekdays: IDropdownOption[];
}

export interface AssetServiceValue {
  type_of_asset: Nullable<IDropdownOption>;
  type_of_repairs: IDropdownOption[];
}

export interface AssetStepValues {
  services: AssetServiceValue[];
}

export interface UsersStepValues {
  focal_points: Array<Nullable<IDropdownOption>>;
}

export interface ReviewStepValues {
  notify_users: string[];
}

export interface FormValues {
  details: DetailsStepValues;
  rules: RulesStepValues;
  time: TimeStepValues;
  asset: AssetStepValues;
  users: UsersStepValues;
  review: ReviewStepValues;
}

export interface FormTempValues {
  details?: Partial<DetailsStepValues>;
  rules?: Partial<RulesStepValues>;
  time?: Partial<TimeStepValues>;
  asset?: Partial<AssetStepValues>;
  users?: Partial<UsersStepValues>;
  review?: Partial<ReviewStepValues>;
}

export type IYupSchema = Yup.SchemaOf<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}>;

export const STEPS = {
  DETAILS: 'details',
  RULES: 'rules',
  TIME: 'time',
  ASSET: 'asset',
  USERS: 'users',
  REVIEW: 'review',
};

export const STEPS_LABELS = {
  DETAILS: 'Details and location',
  RULES: 'Visibility rules',
  TIME: 'Opening time',
  ASSET: 'Type of assets and repairs',
  USERS: 'Facility users',
  REVIEW: 'Review and publish',
};

export const STEPS_ICONS = {
  DETAILS: <MarkerInfoIcon />,
  RULES: <GlobalIcon />,
  TIME: <ClockIcon />,
  ASSET: <GearIcon />,
  USERS: <UserIcon />,
  REVIEW: <ReviewIcon />,
};

export const weekDayOptions = [
  { label: 'Mon', value: 'monday' },
  { label: 'Tue', value: 'tuesday' },
  { label: 'Wed', value: 'wednesday' },
  { label: 'Thu', value: 'thursday' },
  { label: 'Fri', value: 'friday' },
  { label: 'Sat', value: 'saturday' },
  { label: 'Sun', value: 'sunday' },
];

export const MAX_FILE_SIZE = 5; // mb
export const ALLOWED_FORMATS = ['.jpeg', '.jpg'];
export const AGENCIES_RULE_VALUE = '4'; // Note: visible only for one Global Rule with value 4

export const yupSchemas = {
  [STEPS.DETAILS]: Yup.object().shape({
    [STEPS.DETAILS]: Yup.object().shape({
      name: Yup.string().required('Required'),
      country: Yup.mixed().required('Required'),
      city_name: Yup.mixed().required('Required'),
      is_active: Yup.boolean(),
      email: Yup.string().required('Required').email('Email should be valid'),
      depot_number: Yup.mixed().required('Required'),
      latitude: Yup.string()
        .required('Required')
        .test('lat-range', 'Latitude should be between -90 and 90', (value) => {
          const n = Number(value);
          return !Number.isNaN(n) && n >= -90 && n <= 90;
        }),
      longitude: Yup.string()
        .required('Required')
        .test('lng-range', 'Longitude should be between -180 and 180', (value) => {
          const n = Number(value);
          return !Number.isNaN(n) && n >= -180 && n <= 180;
        }),
      address: Yup.string().required('Required'),
      image: Yup.array().test('file-size', `Image should be less than ${MAX_FILE_SIZE} mb`, (files) => {
        if (!files || files.length === 0) return true;
        return files.every((file) => 'size' in file && file.size <= MAX_FILE_SIZE * 1048576);
      }),
    }),
  }),
  [STEPS.RULES]: Yup.object().shape({
    [STEPS.RULES]: Yup.object().shape({
      global: Yup.mixed().required('Required'),
      agencies: Yup.array().when('global', {
        is: (global: Nullable<IDropdownOption>) => global?.value === AGENCIES_RULE_VALUE,
        then: (schema) =>
          schema.min(1, 'At least one agency must be selected').required('At least one agency must be selected'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  }),
  [STEPS.TIME]: Yup.object().shape({
    [STEPS.TIME]: Yup.object().shape({
      start_time: Yup.date().nullable().required('Required'),
      end_time: Yup.date().nullable().required('Required'),
      weekdays: Yup.array().min(1, 'Required').required('Required'),
    }),
  }),
  [STEPS.ASSET]: Yup.object().shape({
    [STEPS.ASSET]: Yup.object().shape({
      services: Yup.array()
        .of(
          Yup.object().shape({
            type_of_asset: Yup.object().nullable().required('Type of asset is required'),
            type_of_repairs: Yup.array()
              .of(
                Yup.object().shape({
                  label: Yup.string().required(),
                  value: Yup.string().required(),
                })
              )
              .min(1, 'Select at least one type of repair')
              .required('Select at least one type of repair'),
          })
        )
        .min(1, 'At least one service is required')
        .test('unique-type-of-asset', 'Each service must have a unique type of asset', (services) => {
          if (!services) return true;
          const values = services.map((s) => s?.type_of_asset?.value).filter(Boolean);
          return new Set(values).size === values.length;
        })
        .required(),
    }),
  }),
  [STEPS.USERS]: Yup.object().shape({
    [STEPS.USERS]: Yup.object().shape({
      focal_points: Yup.array()
        .required('Required')
        .min(1, 'At least one user is required')
        .test('no-null-and-unique', 'Focal points must be unique', (value) => {
          if (!Array.isArray(value)) return false;
          const items = value.filter(Boolean) as IDropdownOption[];
          if (items.length !== value.length) return false;
          const values = items.map((item) => item.value);
          return new Set(values).size === values.length;
        }),
    }),
  }),
  [STEPS.REVIEW]: Yup.object().shape({
    [STEPS.REVIEW]: Yup.object().shape({
      notify_users: Yup.array().of(Yup.string().email('Email should be valid')).notRequired(),
    }),
  }),
};
